import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from '../../i18n';
import { Get, Send } from '../../services/service.api';
import { Eye, EyeOff, Check, ChevronRight, ChevronLeft } from 'lucide-react';

const STEPS = ['Compte', 'Entreprise', 'Confirmation'];

export default function Register() {
  const { t, locale, toggleLocale } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [companies, setCompanies] = useState([]);

  const [form, setForm] = useState({
    email: '', nom: '', prenom: '', tel: '',
    password: '', confirmPassword: '', role: '',
    companyChoice: 'none', // 'none' | 'existing' | 'new'
    company_id: '',
    company_libelle: '', company_capacite: '', company_pays: '',
    company_ville: '', company_description: '', company_domaine: '',
  });

  useEffect(() => {
    // Charger les entreprises existantes
    Get('/companies', '').then(res => {
      if (Array.isArray(res)) setCompanies(res);
    }).catch(() => {});
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Validation step 0
  const validateStep0 = () => {
    if (!form.email || !form.nom || !form.prenom || !form.password || !form.role) {
      toast.error('Tous les champs marqués * sont obligatoires');
      return false;
    }
    if (form.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return false;
    }
    return true;
  };

  const validateStep1 = () => {
    if (form.role === 'partenaire') {
      if (!form.company_id) {
        toast.error('Veuillez sélectionner une entreprise à rejoindre');
        return false;
      }
    }
    if (form.companyChoice === 'new') {
      if (!form.company_libelle) {
        toast.error("Le nom de l'entreprise est obligatoire");
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    setStep(s => s + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let entreprise = null;
      if (form.companyChoice === 'existing' && form.company_id) {
        entreprise = { id: form.company_id };
      } else if (form.companyChoice === 'new' && form.company_libelle && form.role === 'investisseur') {
        entreprise = {
          libelle: form.company_libelle,
          capacite: form.company_capacite,
          pays: form.company_pays,
          ville: form.company_ville,
          description: form.company_description,
          domaine: form.company_domaine,
        };
      }

      const payload = {
        email: form.email,
        nom: form.nom,
        prenom: form.prenom,
        tel: form.tel,
        password: form.password,
        role: form.role,
        entreprise,
      };

      const res = await Send('/auth/register', payload);
      if (res?.error) return toast.error(res.error);
      if (res?.token) {
        dispatch({ type: 'login', value: { user: res.user, token: res.token } });
        if (res.user?.status === 'pending') {
          toast.info('Compte créé ! En attente d\'approbation de l\'investisseur.');
          navigate('/pending');
        } else {
          toast.success('Compte créé avec succès ! 🎉');
          navigate('/dashboard');
        }
      }
    } catch {
      toast.error('Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Lang toggle */}
      <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}>
        <div className="lang-toggle">
          <button className={`lang-btn ${locale === 'fr' ? 'active' : ''}`} onClick={() => locale !== 'fr' && toggleLocale()}>FR</button>
          <button className={`lang-btn ${locale === 'en' ? 'active' : ''}`} onClick={() => locale !== 'en' && toggleLocale()}>EN</button>
        </div>
      </div>

      <div className="auth-card" style={{ maxWidth: 520 }}>
        {/* Logo */}
        <div className="auth-logo" style={{ marginBottom: 24 }}>
          <img src="/logo.png" alt="SavInvest" onError={e => { e.target.style.display = 'none'; }} />
          <div className="auth-logo-title">SavInvest</div>
          <div className="auth-logo-subtitle">{t.auth.registerSubtitle}</div>
        </div>

        {/* Stepper */}
        <div className="stepper">
          {STEPS.map((s, i) => (
            <div key={i} className={`step ${i <= step ? 'done' : ''} ${i === step ? 'active' : ''}`}>
              <div className="step-circle">
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className="step-line" />}
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginBottom: 20, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          Étape {step + 1} — <strong style={{ color: 'var(--text-primary)' }}>{STEPS[step]}</strong>
        </div>

        {/* STEP 0 — Informations personnelles */}
        {step === 0 && (
          <div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{t.auth.prenom} <span className="required">*</span></label>
                <input className="form-control" value={form.prenom}
                  onChange={e => set('prenom', e.target.value)} placeholder="Jean" />
              </div>
              <div className="form-group">
                <label className="form-label">{t.auth.nom} <span className="required">*</span></label>
                <input className="form-control" value={form.nom}
                  onChange={e => set('nom', e.target.value)} placeholder="Dupont" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t.auth.email} <span className="required">*</span></label>
              <input type="email" className="form-control" value={form.email}
                onChange={e => set('email', e.target.value)} placeholder="exemple@mail.com" />
            </div>
            <div className="form-group">
              <label className="form-label">{t.auth.tel}</label>
              <input type="tel" className="form-control" value={form.tel}
                onChange={e => set('tel', e.target.value)} placeholder="+226 70 00 00 00" />
            </div>
            <div className="form-group">
              <label className="form-label">{t.auth.role} <span className="required">*</span></label>
              <select
                className="form-select"
                value={form.role}
                onChange={e => {
                  const r = e.target.value;
                  setForm(f => ({
                    ...f,
                    role: r,
                    companyChoice: r === 'partenaire' ? 'existing' : (f.companyChoice === 'none' ? 'new' : f.companyChoice),
                  }));
                }}
              >
                <option value="">-- Choisir un rôle --</option>
                <option value="investisseur">👑 {t.auth.investor}</option>
                <option value="partenaire">🤝 {t.auth.partner}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t.auth.password} <span className="required">*</span></label>
              <div className="relative">
                <input type={showPwd ? 'text' : 'password'} className="form-control" value={form.password}
                  onChange={e => set('password', e.target.value)} placeholder="Min. 6 caractères"
                  style={{ paddingRight: 44 }} />
                <button type="button" onClick={() => setShowPwd(!showPwd)} style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer'
                }}>
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t.auth.confirmPassword} <span className="required">*</span></label>
              <input type="password" className="form-control" value={form.confirmPassword}
                onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••" />
              {form.confirmPassword && form.password !== form.confirmPassword && (
                <div className="invalid-feedback" style={{ display: 'block' }}>Les mots de passe ne correspondent pas</div>
              )}
            </div>
            <button className="btn btn-primary btn-block" onClick={handleNext} style={{ marginTop: 8 }}>
              {t.common.next} <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* STEP 1 — Entreprise */}
        {step === 1 && (
          <div>
            {/* Choix entreprise — partenaire peut seulement rejoindre */}
            {form.role === 'partenaire' ? (
              <div>
                <div style={{
                  padding: '10px 14px', borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-input)', border: '1px solid var(--border)',
                  fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 16
                }}>
                  🤝 En tant que partenaire, sélectionnez l'entreprise que vous souhaitez rejoindre.
                </div>
                <div className="form-group">
                  <label className="form-label">Sélectionner une entreprise <span className="required">*</span></label>
                  {companies.length > 0 ? (
                    <select className="form-select" value={form.company_id} onChange={e => set('company_id', e.target.value)}>
                      <option value="">-- Choisir une entreprise --</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.libelle} ({c.ville || c.pays || 'N/A'})</option>)}
                    </select>
                  ) : (
                    <div style={{ color: 'var(--warning)', fontSize: '0.85rem' }}>
                      ⚠️ Aucune entreprise disponible actuellement. Un investisseur doit en créer une au préalable.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div className="form-group">
                  <label className="form-label">Votre entreprise</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { value: 'none', label: `🚫 ${t.auth.noCompany}` },
                      { value: 'existing', label: `🏢 ${t.auth.joinExistingCompany}` },
                      { value: 'new', label: `➕ ${t.auth.createNewCompany}` },
                    ].map(opt => (
                      <label key={opt.value} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '12px 14px', borderRadius: 'var(--radius-sm)',
                        border: `1px solid ${form.companyChoice === opt.value ? 'var(--primary)' : 'var(--border)'}`,
                        background: form.companyChoice === opt.value ? 'var(--primary-glow)' : 'var(--bg-input)',
                        cursor: 'pointer', transition: 'var(--transition)', fontSize: '0.9rem'
                      }}>
                        <input type="radio" value={opt.value} checked={form.companyChoice === opt.value}
                          onChange={() => set('companyChoice', opt.value)} style={{ accentColor: 'var(--primary)' }} />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Entreprise existante */}
                {form.companyChoice === 'existing' && (
                  <div className="form-group">
                    <label className="form-label">Sélectionner une entreprise</label>
                    <select className="form-select" value={form.company_id} onChange={e => set('company_id', e.target.value)}>
                      <option value="">-- Choisir --</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.libelle} ({c.ville || c.pays || 'N/A'})</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Nouvelle entreprise */}
            {form.companyChoice === 'new' && (
              <>
                <div className="form-group">
                  <label className="form-label">{t.company.libelle} <span className="required">*</span></label>
                  <input className="form-control" value={form.company_libelle}
                    onChange={e => set('company_libelle', e.target.value)} placeholder="Nom de l'entreprise" />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">{t.company.pays}</label>
                    <input className="form-control" value={form.company_pays}
                      onChange={e => set('company_pays', e.target.value)} placeholder="Burkina Faso" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t.company.ville}</label>
                    <input className="form-control" value={form.company_ville}
                      onChange={e => set('company_ville', e.target.value)} placeholder="Ouagadougou" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">{t.company.domaine}</label>
                  <input className="form-control" value={form.company_domaine}
                    onChange={e => set('company_domaine', e.target.value)} placeholder="Commerce, Agriculture, Textile..." />
                </div>
                <div className="form-group">
                  <label className="form-label">{t.company.description}</label>
                  <textarea className="form-control" value={form.company_description} rows={2}
                    onChange={e => set('company_description', e.target.value)} />
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button className="btn btn-outline" onClick={() => setStep(0)}>
                <ChevronLeft size={16} /> {t.common.previous}
              </button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setStep(2)}>
                {t.common.next} <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 — Confirmation */}
        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <div style={{
              background: 'var(--bg-input)', borderRadius: 'var(--radius)', padding: 16,
              display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20
            }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>
                Récapitulatif
              </h3>
              {[
                { label: 'Prénom Nom', value: `${form.prenom} ${form.nom}` },
                { label: 'Email', value: form.email },
                { label: 'Rôle', value: form.role === 'investisseur' ? '💼 Investisseur' : '🤝 Partenaire' },
                form.tel && { label: 'Téléphone', value: form.tel },
                form.companyChoice === 'new' && form.company_libelle && { label: 'Entreprise', value: `${form.company_libelle} (nouvelle)` },
                form.companyChoice === 'existing' && form.company_id && { label: 'Entreprise', value: 'Entreprise existante' },
              ].filter(Boolean).map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{item.label}</span>
                  <span style={{ fontWeight: 600 }}>{item.value}</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" className="btn btn-outline" onClick={() => setStep(1)}>
                <ChevronLeft size={16} /> {t.common.previous}
              </button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={loading}>
                {loading ? <span className="spinner" /> : <Check size={16} />}
                {loading ? 'Création...' : 'Créer mon compte'}
              </button>
            </div>
          </form>
        )}

        <div className="divider" />
        <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          {t.auth.alreadyHaveAccount}{' '}
          <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
            {t.auth.login}
          </Link>
        </p>
      </div>
    </div>
  );
}
